// Écran Territoire : la vraie carte de la collectivité (limites OpenStreetMap, fond vectoriel
// libre), tout ce qui est hors du territoire grisé, échelles Gabon, province, commune et
// arrondissements, dossiers localisés numérotés comme dans la liste voisine, sources et licences.
import type { Dossier } from '@cc/contrats';
import { Bandeau, CarteTerritoire, EtatErreur, EtatVide, Lien, Squelette, numeroMarqueur } from '@cc/ui';
import { useEffect, useMemo, useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { date as formaterDate } from '../../commun/format';
import { construireEchelles, fichierPourEchelle, fichiersInitiaux, marqueursDossiers, tonDossier, useDossiersLocalises, useLimites, useManifeste, useTerritoire, type Echelles, type NomFichier } from './donnees';
import styles from './territoire.module.css';

const LIBELLES_TON = { ouvert: 'Dossier ouvert', retard: 'En retard', autre: 'Terminé' } as const;

function ListeDossiers({ dossiers, actif, onChoisir }: { dossiers: Dossier[]; actif: string | null; onChoisir: (id: string) => void }) {
  if (dossiers.length === 0) {
    return (
      <EtatVide titre="Aucun dossier localisé">
        Les dossiers prennent place sur la carte dès qu'une position est saisie au dépôt (guichet, portail ou terrain). Aucune position n'est jamais placée à la main par la plateforme.
      </EtatVide>
    );
  }
  return (
    <ol className={styles.liste} aria-label="Dossiers localisés">
      {dossiers.map((d, i) => (
        <li key={d.id}>
          <button type="button" className={styles.element} aria-pressed={actif === d.id} onClick={() => onChoisir(d.id)}>
            <span className={`${styles.numero} ${styles[tonDossier(d)]}`} aria-hidden>
              {numeroMarqueur(i + 1)}
            </span>
            <span className={styles.corps}>
              <span className="cc-mono">{d.numero ?? 'Sans numéro'}</span>
              <span className={styles.titre}>{d.demarche.libelle}</span>
              <span className={styles.sous}>
                {[d.demandeur.nom, d.arrondissement?.nom, d.echeance_interne ? `échéance ${formaterDate(d.echeance_interne)}` : null].filter(Boolean).join(' · ')}
              </span>
              <span className={styles.sous}>
                {LIBELLES_TON[tonDossier(d)]} · {d.statut_usager_libelle} · précision : {d.localisation?.precision ?? 'inconnue'}
              </span>
            </span>
          </button>
          {actif === d.id ? (
            <Lien href={`/dossiers/${d.id}`} className={styles.ouvrir}>
              Ouvrir le dossier
            </Lien>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function Sources({ echelles }: { echelles: Echelles }) {
  const manifeste = useManifeste();
  const indicatives = echelles.echelles.filter((e) => e.indicatif);
  return (
    <section className={styles.sources} aria-labelledby="sources-carte">
      <h2 id="sources-carte">Sources et licences</h2>
      <ul>
        <li>Fond de carte : OpenFreeMap (tuiles vectorielles OpenMapTiles), données © les contributeurs d&apos;OpenStreetMap, licence ODbL. Aucune clé ni compte.</li>
        <li>
          Limites du Gabon, des provinces, des départements, de la commune de Libreville et des arrondissements disponibles : OpenStreetMap (relations administratives, licence ODbL)
          {manifeste.data?.extraction_osm ? `, extraction du ${manifeste.data.extraction_osm.slice(0, 10)}` : ''}.
        </li>
        {indicatives.length > 0 ? (
          <li>
            Limites indicatives (aucune source officielle ni libre) : {indicatives.map((e) => e.libelle).join(', ')}. Tracé de maquette, dessiné à la main, sans valeur administrative.
          </li>
        ) : null}
        <li>Positions des dossiers : saisies au dépôt, en WGS84 avec leur précision. Aucune position n&apos;est placée à la main.</li>
      </ul>
    </section>
  );
}

export function PageTerritoire() {
  const territoire = useTerritoire();
  const [supplementaires, setSupplementaires] = useState<NomFichier[]>([]);
  const fichiers = useMemo(() => [...new Set([...fichiersInitiaux(territoire.data), ...supplementaires])], [territoire.data, supplementaires]);
  const { limites, presentation, chargement, erreur } = useLimites(fichiers, fichiersInitiaux(territoire.data));
  const dossiers = useDossiersLocalises();
  const [echelle, setEchelleBrute] = useState<string | null>(null);
  // Les fichiers nationaux (pays, provinces) ne sont téléchargés que si l'échelle est demandée.
  const setEchelle = (id: string) => {
    const f = fichierPourEchelle(id);
    if (f) setSupplementaires((s) => (s.includes(f) ? s : [...s, f]));
    setEchelleBrute(id);
  };
  const [actif, setActif] = useState<string | null>(null);
  const echelles = useMemo(() => (territoire.data && !chargement ? construireEchelles(territoire.data, limites, presentation) : null), [territoire.data, chargement, limites, presentation]);
  useEffect(() => {
    if (echelles && !echelle) setEchelleBrute(echelles.defaut);
  }, [echelles, echelle]);
  const liste = dossiers.data ?? [];
  const marqueurs = useMemo(() => marqueursDossiers(liste), [liste]);
  const c = territoire.data?.collectivite;
  const description = c ? `${c.nom}, province de ${c.province.nom}${echelles?.communeAffichee && !echelles.mention ? `, limites de ${echelles.communeAffichee}` : ''}.` : 'Carte de la collectivité.';

  return (
    <>
      <EnTetePage etiquette="Territoire" titre="Carte du territoire" description={description} />
      {territoire.isError ? <EtatErreur message="Le territoire de la collectivité n'a pas pu être chargé." onReessayer={() => void territoire.refetch()} /> : null}
      {erreur ? <Bandeau type="erreur">Les limites administratives n&apos;ont pas pu être chargées : la carte s&apos;affiche sans contour.</Bandeau> : null}
      {echelles?.mention ? <Bandeau type="info">{echelles.mention}</Bandeau> : null}
      {dossiers.isError ? <Bandeau type="info">Les dossiers localisés ne sont pas disponibles (droits ou module inactif) : la carte s&apos;affiche sans marqueur.</Bandeau> : null}
      <div className={styles.disposition}>
        <div className={styles.carte}>
          {echelles && echelle ? (
            <CarteTerritoire titre="Carte du territoire" echelles={echelles.echelles} echelle={echelle} onEchelle={setEchelle} marqueurs={marqueurs} marqueurActif={actif} onMarqueur={setActif} hauteur="min(70vh, 640px)" />
          ) : (
            <Squelette hauteurBloc={440} libelle="Chargement de la carte" />
          )}
        </div>
        <aside className={styles.cote} aria-label="Dossiers localisés">
          <div className={styles.coteEntete}>
            <h2>Dossiers localisés</h2>
            <span className="cc-mono">{liste.length}</span>
          </div>
          {dossiers.isPending ? <Squelette lignes={4} /> : <ListeDossiers dossiers={liste} actif={actif} onChoisir={setActif} />}
        </aside>
      </div>
      {echelles ? <Sources echelles={echelles} /> : null}
    </>
  );
}
